import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, withIdempotencyKey } from "./client";

export interface TransactionListItem {
  id: string;
  kind: "income" | "expense";
  reference: string;
  date: string;
  party: string;
  programme: string;
  amount: string;
  status: string;
  displayStatus: string;
  partial: boolean;
}

export interface ExpenseLineDto {
  id: string;
  unitId: string;
  fundId: string;
  categoryId: string;
  costType: string;
  amount: string;
}

export interface ExpenseActorDto {
  id: string;
  name: string;
}

export interface ExpenseDto {
  id: string;
  referenceNo: string;
  payee: string;
  expenseDate: string;
  description: string | null;
  status: string;
  total: string;
  paidAmount: string;
  unpaidAmount: string;
  missingEvidenceReason: string | null;
  files: {
    id: string;
    originalName: string;
    mimeType: string;
    sizeBytes: number;
    createdAt: string;
  }[];
  lines: ExpenseLineDto[];
  createdById: string;
  createdBy: ExpenseActorDto;
  approvedById: string | null;
  approvedBy: ExpenseActorDto | null;
  approvedAt: string | null;
  rejectionReason: string | null;
}

export interface ReceiptDto {
  id: string;
  voucherNo: string;
  source: string;
  receiptDate: string;
  bankAccountId: string;
  total: string;
  note: string | null;
  status: string;
  lines: {
    id: string;
    unitId: string;
    fundId: string;
    categoryId: string;
    amount: string;
  }[];
}

export interface PaymentDto {
  id: string;
  voucherNo: string;
  bankAccountId: string;
  amount: string;
  paymentDate: string;
  reference: string | null;
  lines: { id: string; expenseLineId: string; amount: string }[];
}

export type TransactionTab =
  | "all"
  | "income"
  | "expense"
  | "submitted"
  | "approved"
  | "part_paid"
  | "paid"
  | "draft"
  | "evidence";

export const transactionsQueryKey = (
  tab: TransactionTab,
  search: string,
) => ["transactions", tab, search] as const;

export function useTransactionsQuery(tab: TransactionTab, search: string) {
  const params = new URLSearchParams();
  if (tab !== "all") {
    params.set("tab", tab);
  }
  if (search.trim()) {
    params.set("search", search.trim());
  }
  const query = params.toString();

  return useQuery({
    queryKey: transactionsQueryKey(tab, search),
    queryFn: () =>
      api.get<{ transactions: TransactionListItem[] }>(
        `/transactions${query ? `?${query}` : ""}`,
      ),
  });
}

export function useExpenseQuery(id: string | null) {
  return useQuery({
    queryKey: ["expense", id],
    queryFn: () => api.get<{ expense: ExpenseDto }>(`/expenses/${id}`),
    enabled: Boolean(id),
  });
}

export function useReceiptQuery(id: string | null) {
  return useQuery({
    queryKey: ["receipt", id],
    queryFn: () => api.get<{ receipt: ReceiptDto }>(`/receipts/${id}`),
    enabled: Boolean(id),
  });
}

export function useExpensePaymentsQuery(expenseId: string | null) {
  return useQuery({
    queryKey: ["expense-payments", expenseId],
    queryFn: () =>
      api.get<{ payments: PaymentDto[] }>(`/expenses/${expenseId}/payments`),
    enabled: Boolean(expenseId),
  });
}

export function useCreateReceiptMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: unknown) =>
      api.post<{ receipt: ReceiptDto }>("/receipts", body, withIdempotencyKey()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["transactions"] });
    },
  });
}

export function useCreateExpenseMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: unknown) =>
      api.post<{ expense: ExpenseDto }>("/expenses", body, withIdempotencyKey()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["transactions"] });
    },
  });
}

export function useUpdateExpenseMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: unknown }) =>
      api.patch<{ expense: ExpenseDto }>(`/expenses/${id}`, body),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["transactions"] });
      void queryClient.invalidateQueries({ queryKey: ["expense", variables.id] });
      void queryClient.invalidateQueries({ queryKey: ["overview"] });
    },
  });
}

export function useSubmitExpenseMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      missingEvidenceReason,
    }: {
      id: string;
      missingEvidenceReason?: string;
    }) =>
      api.post<{ expense: ExpenseDto }>(`/expenses/${id}/submit`, {
        missingEvidenceReason,
      }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["transactions"] });
      void queryClient.invalidateQueries({ queryKey: ["expense", variables.id] });
    },
  });
}

export function useApproveExpenseMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<{ expense: ExpenseDto }>(`/expenses/${id}/approve`),
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: ["transactions"] });
      void queryClient.invalidateQueries({ queryKey: ["expense", id] });
    },
  });
}

export function useRejectExpenseMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post<{ expense: ExpenseDto }>(`/expenses/${id}/reject`, { reason }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["transactions"] });
      void queryClient.invalidateQueries({ queryKey: ["expense", variables.id] });
    },
  });
}

export function useWithdrawExpenseMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      api.post<{ expense: ExpenseDto }>(`/expenses/${id}/withdraw`, { reason }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["transactions"] });
      void queryClient.invalidateQueries({ queryKey: ["expense", variables.id] });
      void queryClient.invalidateQueries({ queryKey: ["overview"] });
    },
  });
}

export function useReverseExpenseMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post<{ expense: ExpenseDto }>(`/expenses/${id}/reverse`, { reason }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["transactions"] });
      void queryClient.invalidateQueries({ queryKey: ["expense", variables.id] });
      void queryClient.invalidateQueries({ queryKey: ["overview"] });
    },
  });
}

export function useTransactionQuery(id: string | null) {
  return useQuery({
    queryKey: ["transaction", id],
    queryFn: () =>
      api.get<
        | { kind: "income"; transaction: TransactionListItem; receipt: ReceiptDto }
        | { kind: "expense"; transaction: TransactionListItem; expense: ExpenseDto }
      >(`/transactions/${id}`),
    enabled: Boolean(id),
  });
}

export function useRecordPaymentMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: unknown }) =>
      api.post<{ payment: PaymentDto; expense: ExpenseDto }>(
        `/expenses/${id}/payments`,
        body,
        withIdempotencyKey(),
      ),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["transactions"] });
      void queryClient.invalidateQueries({ queryKey: ["expense", variables.id] });
      void queryClient.invalidateQueries({
        queryKey: ["expense-payments", variables.id],
      });
    },
  });
}

export function useReverseReceiptMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post<{ receipt: ReceiptDto }>(`/receipts/${id}/reverse`, { reason }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["transactions"] });
      void queryClient.invalidateQueries({ queryKey: ["receipt", variables.id] });
    },
  });
}

export function useReversePaymentMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post<{ payment: PaymentDto }>(`/payments/${id}/reverse`, { reason }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["transactions"] });
      void queryClient.invalidateQueries({ queryKey: ["expense-payments"] });
      void queryClient.invalidateQueries({ queryKey: ["expense"] });
    },
  });
}

export interface TransferDto {
  id: string;
  voucherNo: string;
  fromBankAccountId: string;
  toBankAccountId: string;
  amount: string;
  transferDate: string;
}

export function useCreateTransferMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      fromBankAccountId: string;
      toBankAccountId: string;
      amount: string;
      transferDate: string;
    }) => api.post<{ transfer: TransferDto }>("/transfers", body, withIdempotencyKey()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["transactions"] });
      void queryClient.invalidateQueries({ queryKey: ["overview"] });
    },
  });
}
